import { useNavigate } from "react-router-dom";
import {
  CheckIcon,
  LockClosedIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  LinkIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useEmailVerification } from "../../hooks/useEmailVerification";
import { useResendTimer } from "../../hooks/useResendTimer";
import { resendVerificationEmail } from "../../api/authApi";
import { useState } from "react";
import { getBrandName } from "../../utility/branding";
import VerifyCodeInput from "../Authentication/VerifyCodeInput";

const STEPS = [
  {
    key: "availability",
    progressKey: "availabilityCompleted",
    title: "Set your availability",
    description: "Tell us when you're free to take appointments",
    cta: "Set Availability",
    route: "/availability",
    icon: CalendarDaysIcon,
    time: "~2 min",
  },
  {
    key: "services",
    progressKey: "serviceCompleted",
    title: "Add your services",
    description: "Define what you offer, how long it takes, and pricing",
    cta: "Add a Service",
    route: "/services",
    icon: WrenchScrewdriverIcon,
    time: "~2 min",
  },
  {
    key: "bookingPage",
    progressKey: "bookingLinkCompleted",
    title: "Create your booking link",
    description: "Choose your booking link and go live",
    cta: "Set Up Booking Link",
    route: "/booking-page/general",
    icon: LinkIcon,
    time: "~1 min",
  },
];

const OnboardingDashboard = ({ setupProgress }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEmailVerified } = useEmailVerification();
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [sendError, setSendError] = useState(null);
  const { startCooldown } = useResendTimer(120, "verifyCodeResendTimer:dashboard");

  // Sends the OTP on demand, then reveals the code-entry widget. A code is only
  // ever sent when the user clicks "Send code" — never automatically on mount.
  const { mutate: sendCode, isPending: isSending } = useMutation({
    mutationFn: () => {
      // Guard against firing with email: undefined (user not yet loaded / session
      // expired), which would 500 the backend instead of sending a code.
      if (!user?.email) {
        throw new Error("NO_EMAIL");
      }
      return resendVerificationEmail({ email: user.email });
    },
    onSuccess: () => {
      setSendError(null);
      startCooldown();
      setShowCodeEntry(true);
    },
    onError: () => {
      setSendError("Couldn't send a code. Please try again.");
    },
  });

  const completedCount = STEPS.filter(
    (step) => setupProgress[step.progressKey]
  ).length;

  const getStepState = (stepIndex) => {
    const step = STEPS[stepIndex];
    if (setupProgress[step.progressKey]) return "completed";

    // Check if all previous steps are completed
    for (let i = 0; i < stepIndex; i++) {
      if (!setupProgress[STEPS[i].progressKey]) return "locked";
    }
    return "current";
  };

  return (
    <div className="px-2 py-6 sm:px-4 max-w-2xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ink-900">
          Welcome to {getBrandName()}{user?.firstName ? `, ${user.firstName}` : ""}!
        </h1>
        <p className="mt-2 text-sm text-ink-600">
          Let's get your booking system ready. This usually takes about 5
          minutes.
        </p>
        {/* Progress indicator */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 bg-cream-300 rounded-full h-2">
            <div
              className="bg-accent-500 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${(completedCount / STEPS.length) * 100}%`,
              }}
            ></div>
          </div>
          <span className="text-sm font-medium text-ink-500">
            {completedCount}/{STEPS.length}
          </span>
        </div>
      </div>

      {/* Step Cards */}
      <div className="space-y-3">
        {STEPS.map((step, index) => {
          const state = getStepState(index);
          const Icon = step.icon;
          const isLocked = state === "locked";
          const isCompleted = state === "completed";
          const isCurrent = state === "current";

          return (
            <div
              key={step.key}
              className={`relative rounded-xl border p-5 transition-all ${
                isCompleted
                  ? "bg-white border-ink-100"
                  : isCurrent
                    ? "bg-white border-accent-200 shadow-sm ring-1 ring-accent-100"
                    : "bg-cream-100 border-ink-100 opacity-60"
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Step Number / Status */}
                <div className="flex-shrink-0 pt-0.5">
                  {isCompleted ? (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500">
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                  ) : isCurrent ? (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-accent-500 bg-white">
                      <span className="text-sm font-semibold text-accent-500">
                        {index + 1}
                      </span>
                    </div>
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-ink-200 bg-white">
                      <LockClosedIcon className="h-3.5 w-3.5 text-ink-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3
                        className={`text-sm font-semibold ${
                          isCompleted
                            ? "text-ink-500"
                            : isCurrent
                              ? "text-ink-900"
                              : "text-ink-400"
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p
                        className={`text-sm mt-0.5 ${
                          isCompleted
                            ? "text-ink-400"
                            : isCurrent
                              ? "text-ink-500"
                              : "text-ink-400"
                        }`}
                      >
                        {isLocked
                          ? `Complete step ${index} first`
                          : step.description}
                      </p>
                    </div>

                    {/* Time estimate */}
                    {isCurrent && (
                      <span className="hidden sm:inline-flex text-xs text-ink-400 flex-shrink-0 ml-4">
                        {step.time}
                      </span>
                    )}
                  </div>

                  {/* CTA Button */}
                  {isCurrent && (
                    <button
                      onClick={() => navigate(step.route)}
                      className="mt-3 inline-flex items-center rounded-md bg-accent-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-accent-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                    >
                      <Icon className="h-4 w-4 mr-1.5" />
                      {step.cta}
                    </button>
                  )}

                  {/* Completed label */}
                  {isCompleted && (
                    <span className="mt-2 inline-flex text-xs font-medium text-accent-500">
                      Completed
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Email Verification Nudge — expands an inline code field in place */}
      {!isEmailVerified && (
        <div className="mt-6 rounded-lg bg-accent-100 border border-accent-100 p-4">
          <div className="flex items-start gap-3">
            <EnvelopeIcon className="h-5 w-5 text-accent-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              {showCodeEntry ? (
                <VerifyCodeInput
                  email={user?.email}
                  initialSent
                  cooldownKey="verifyCodeResendTimer:dashboard"
                  label="Enter the 6-digit code we emailed you to publish your page"
                  onSuccess={() => setShowCodeEntry(false)}
                />
              ) : (
                <div>
                  <p className="text-sm text-accent-700">
                    <span className="font-medium">
                      Verify your email before going live.
                    </span>{" "}
                    We'll send a 6-digit code to{" "}
                    <span className="font-semibold">{user?.email}</span>.{" "}
                    <button
                      type="button"
                      onClick={() => sendCode()}
                      disabled={isSending}
                      className="underline font-medium text-accent-600 hover:text-accent-500 disabled:text-ink-400 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      {isSending ? "Sending…" : "Send code"}
                    </button>
                  </p>
                  {sendError && (
                    <p className="mt-1.5 text-sm text-signal-red-600" role="alert">
                      {sendError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingDashboard;
